<template>
  <div
    class="flex flex-row justify-between px-2 py-0.5 lg:py-3 lg:pl-5 lg:pr-3 gap-2 bg-gradient-gray-neutral border-b border-secondary items-center"
  >
    <div class="flex flex-row gap-1 items-center">
      <Recurring v-if="!!task.recurrence" :size="maxTablet ? 'lg' : 'md'" />
      <TaskVisibility
        :task="task"
        :readonly="readonly || !canEdit"
        @update="$emit('update', $event)"
      />
    </div>
    <div v-if="maxTablet" class="flex flex-row gap-1 items-center">
      <MobileActionsModal
        :task="task"
        :canEdit="canEdit"
        :canLogTime="canLogTime"
        :canLogMaterial="canLogMaterial"
        :canCreateChecklist="canCreateChecklist"
        :isArchived="isArchived"
        :readonly="readonly"
        @logTime="$emit('logTime')"
        @logMaterial="$emit('logMaterial')"
        @addChecklist="$emit('addChecklist')"
        @translate="$emit('translate')"
        @delete="$emit('delete')"
      >
        <IconButton variant="tertiary" type="gray" size="lg">
          <Icon name="dots-horizontal" size="xl" />
        </IconButton>
      </MobileActionsModal>
      <DialogClose
        :as="CloseButton"
        size="lg"
        :aria-label="$t('components.buttons.close')"
      />
    </div>
    <div v-else class="flex flex-row gap-2">
      <div class="flex flex-row gap-0.5">
        <template
          v-if="!task.time?.length && !task.material?.length && !readonly"
        >
          <Tooltip
            v-if="canLogTime"
            :title="$t('tooltips.task_passport.actions.log_time')"
            side="bottom"
            disableHoverableContent
          >
            <Button
              variant="tertiary"
              type="gray"
              icon
              data-id="task.log-time"
              @click="$emit('logTime')"
            >
              <Icon name="clock-add" size="xl" />
            </Button>
          </Tooltip>
          <Tooltip
            v-if="canLogMaterial"
            :title="$t('tooltips.task_passport.actions.log_material')"
            side="bottom"
            disableHoverableContent
          >
            <Button
              variant="tertiary"
              type="gray"
              icon
              data-id="task.log-material"
              @click="$emit('logMaterial')"
            >
              <Icon name="euro-circle" size="xl" />
            </Button>
          </Tooltip>
        </template>
        <Tooltip
          v-if="
            !task.checklist?.items?.length && canCreateChecklist && !readonly
          "
          :title="$t('tooltips.task_passport.actions.add_checklist')"
          side="bottom"
          disableHoverableContent
        >
          <Button
            variant="tertiary"
            type="gray"
            icon
            data-id="task.add-checklist"
            @click="$emit('addChecklist')"
          >
            <Icon name="checklist" size="xl" />
          </Button>
        </Tooltip>
        <Tooltip
          :title="$t('tooltips.task_passport.actions.translate')"
          side="bottom"
          disableHoverableContent
        >
          <Button
            variant="tertiary"
            type="gray"
            icon
            data-id="task.translate"
            @click="$emit('translate')"
          >
            <Icon name="translate" size="xl" />
          </Button>
        </Tooltip>
        <ShareModal v-if="canEdit" :task="task">
          <Tooltip
            :title="$t('tooltips.task_passport.actions.share_link')"
            side="bottom"
            disableHoverableContent
          >
            <Button
              variant="tertiary"
              type="gray"
              icon
              data-id="task.share-url"
            >
              <Icon name="link-default" size="xl" />
            </Button>
          </Tooltip>
        </ShareModal>
        <DeleteModal
          v-if="canEdit && !isArchived"
          :title="$t('components.task_details.delete.title')"
          :description="$t('components.task_details.delete.description')"
          :confirmLabel="$t('components.task_details.delete.button')"
          @delete="$emit('delete')"
        >
          <Tooltip
            :title="$t('tooltips.task_passport.actions.archive')"
            side="bottom"
            disableHoverableContent
          >
            <Button variant="tertiary" type="gray" icon data-id="task.archive">
              <Icon name="delete" size="xl" />
            </Button>
          </Tooltip>
        </DeleteModal>
      </div>
      <TaskPrimaryAction
        :task="task"
        :canComplete="canComplete"
        :busy="busy"
        @click="$emit('primaryAction', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { Recurring } from "@/SharedComponents/Badges";
import { Button, CloseButton, IconButton } from "@/SharedComponents/Buttons";
import { DialogClose } from "@/SharedComponents/Dialog";
import { Icon } from "@/SharedComponents/Icon";
import { DeleteModal, ShareModal } from "@/SharedComponents/Modals";
import { TaskPrimaryAction } from "@/SharedComponents/Tasks";
import {
  MobileActionsModal,
  TaskVisibility,
} from "@/SharedComponents/Tasks/TaskDetailsParts";
import { Tooltip } from "@/SharedComponents/Tooltips";
import { useBreakpoints } from "@/Composables/useBreakpoints";

const { maxTablet } = useBreakpoints();

defineEmits([
  "update",
  "logTime",
  "logMaterial",
  "addChecklist",
  "translate",
  "delete",
  "primaryAction",
]);

defineProps({
  task: { type: Object, required: true },
  canEdit: { type: Boolean, default: false },
  canLogTime: { type: Boolean, default: false },
  canLogMaterial: { type: Boolean, default: false },
  canCreateChecklist: { type: Boolean, default: false },
  canComplete: { type: Boolean, default: false },
  isArchived: { type: Boolean, default: false },
  readonly: { type: Boolean, required: false },
  busy: { type: Boolean, required: false },
});
</script>
